import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ContainedButton from '../../../components/base/ContainedButton';
import { useMenu } from '../../../components/base/useMenu';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TextButton from '../../../components/base/TextButton';
import { useTheme } from '@emotion/react';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Fade from '@mui/material/Fade';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TableChartIcon from '@mui/icons-material/TableChart';
import Paper from '@mui/material/Paper';

const ExcelTestCaseReports = ({ onExportTestCase, isExportTestCases, disabled, mode, setMode }) => {
  const { menuProps, triggerProps } = useMenu();
  const theme = useTheme();

  const modeOptions = [
    {
      value: 'simple',
      label: 'Simple',
      description: 'Basic test case information',
      icon: <AutoAwesomeIcon fontSize="small" />,
      color: theme.palette.success.main
    },
    {
      value: 'advanced',
      label: 'Advanced',
      description: 'Complete test data with steps',
      icon: <SettingsIcon fontSize="small" />,
      color: theme.palette.secondary.main
    }
  ];

  return (
    <Box>
      <Tooltip
        title={disabled ? 'Select test cases to export' : 'Export to Excel'}
        arrow
        TransitionComponent={Fade}>
        <span>
          <TextButton
            {...triggerProps}
            loadingProps={{ size: 16 }}
            disabled={disabled}
            startIcon={
              <CloudDownloadIcon
                sx={{
                  color: disabled ? theme.palette.action.disabled : theme.palette.primary.main
                }}
              />
            }
            isLoading={isExportTestCases}
            sx={{
              '&:hover:not(.Mui-disabled)': {
                backgroundColor: theme.palette.action.hover
              }
            }}>
            Export Excel
          </TextButton>
        </span>
      </Tooltip>

      <Popover
        {...menuProps}
        elevation={2}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 340,
            maxWidth: 380,
            backgroundColor: theme.palette.background.paper
          }
        }}>
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
            <TableChartIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Export to Excel
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 4 }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  mb: 2,
                  color: theme.palette.text.primary,
                  fontWeight: 500,
                  fontSize: '0.875rem'
                }}>
                Select Export Mode
              </FormLabel>

              <RadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value)}
                sx={{
                  gap: 1.5,
                  '& .MuiFormControlLabel-root': {
                    margin: 0
                  }
                }}>
                {modeOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio sx={{ display: 'none' }} />}
                    label={
                      <Paper
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          width: '100%',
                          p: 2,
                          borderColor: mode === option.value ? option.color : theme.palette.divider,
                          borderWidth: mode === option.value ? 2 : 1,
                          borderRadius: 2,
                          backgroundColor:
                            mode === option.value
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.05)'
                                : 'rgba(0, 0, 0, 0.02)'
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          '&:hover': {
                            borderColor: option.color,
                            backgroundColor:
                              theme.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.08)'
                                : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                        onClick={() => setMode(option.value)}>
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            backgroundColor: option.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: mode === option.value ? 1 : 0.8
                          }}>
                          {React.cloneElement(option.icon, {
                            sx: { color: 'white' }
                          })}
                        </Box>

                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600}>
                              {option.label}
                            </Typography>
                            {mode === option.value && (
                              <CheckCircleIcon
                                sx={{
                                  color: theme.palette.success.main,
                                  fontSize: '1rem'
                                }}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        </Box>
                      </Paper>
                    }
                    sx={{ m: 0 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <ContainedButton
            fullWidth
            size="large"
            onClick={() => {
              onExportTestCase();
              menuProps.onClose();
            }}
            startIcon={<CloudDownloadIcon />}
            disabled={disabled}
            loading={isExportTestCases}
            sx={{
              borderRadius: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.25
            }}>
            {isExportTestCases
              ? 'Exporting...'
              : `Export ${mode === 'advanced' ? 'Advanced' : 'Simple'} Excel`}
          </ContainedButton>
        </Box>
      </Popover>
    </Box>
  );
};

export default ExcelTestCaseReports;
